import { Component, OnInit, inject, effect } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, ActivatedRoute } from '@angular/router';
import { BrandingService } from '@next/core-lib/branding';
import { SiteActions, SiteSelectors } from '@next/core-lib/site';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { listAnimations } from '../../../animations';
import { SiteState } from '../../../shared/models/shared.model';
import { ObservableTableDataSource } from '../../../shared/table/observable-table-data-source';

import { Contactperson } from '../../models/contactperson.model';
import {
  searchContactpersons,
  clearSelectedContactperson,
} from '../../store/contactperson.actions';
import {
  getAllContactpersons,
  getSearchText,
  selectLoadingContactpersons,
} from '../../store/contactperson.selectors';

@Component({
  selector: 'app-contactperson-overview',
  templateUrl: './contactperson-overview.component.html',
  styleUrls: ['./contactperson-overview.component.scss'],
  animations: [listAnimations],
  standalone: false,
})
export class ContactpersonOverviewComponent implements OnInit {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly brandingService = inject(BrandingService);

  contactpersons$: Observable<Contactperson[]> = this.store.select(getAllContactpersons);
  readonly contactpersonsDataSource = new ObservableTableDataSource(this.contactpersons$);
  readonly displayedColumns = ['name', 'address', 'email'];
  emptyContactpersons$: Observable<boolean> = this.contactpersons$.pipe(
    map((contactpersons) => !contactpersons || contactpersons.length === 0),
  );
  readonly pageTitle = toSignal(this.store.select(SiteSelectors.selectPageTitle));
  searchTextValue$: Observable<string> = this.store.select(getSearchText);
  isLoading$: Observable<boolean> = this.store.select(selectLoadingContactpersons);
  isAgrifirm: boolean = this.brandingService.isTenant('Agrifirm');

  constructor() {
    effect(() => {
      const title = this.activatedRoute.snapshot.data['pageTitle'];
      if (title) {
        this.store.dispatch(SiteActions.updatePageTitle({ pageTitle: title }));
      }
    });
  }

  ngOnInit() {
    this.store.dispatch(clearSelectedContactperson());
    this.store.dispatch(searchContactpersons({ searchtext: '' }));
  }

  add = () => this.router.navigate(['add'], { relativeTo: this.activatedRoute });

  openContactperson(contactperson: Contactperson) {
    this.router.navigate(['/contactperson', contactperson.id]);
  }

  getName(contactperson: Contactperson) {
    return [contactperson.firstName, contactperson.lastName].filter(Boolean).join(' ');
  }

  getAddress(contactperson: Contactperson) {
    return [contactperson.street, contactperson.houseNumber, contactperson.houseNumberAddition]
      .filter(Boolean)
      .join(' ');
  }

  getCity(contactperson: Contactperson) {
    return [contactperson.postalCode, contactperson.city].filter(Boolean).join(' ');
  }

  searchValueChanged(searchValue: string) {
    return this.store.dispatch(searchContactpersons({ searchtext: searchValue }));
  }
}
